import { useState, useEffect } from "react";
import { api } from "../utils/api";
import {
  Package, MapPin, Phone, Check, X, Loader2,
  RefreshCw, Bike, Clock, CheckCircle2, XCircle,
  Eye, FileText, Gift, Weight, Box, Search,
  Truck, User, ChevronRight, AlertCircle
} from "lucide-react";

const T = {
  primary:"#f97316", primaryDark:"#ea580c", primaryLight:"#fff7ed", primaryMid:"#fed7aa",
  white:"#ffffff", bg:"#f1f5f9", text:"#0f172a", textSub:"#64748b", textMuted:"#94a3b8",
  border:"#e2e8f0", green:"#16a34a", greenLight:"#dcfce7", red:"#ef4444", redLight:"#fef2f2",
  yellow:"#f59e0b", yellowLight:"#fefce8", blue:"#2563eb", blueLight:"#eff6ff",
  purple:"#7c3aed", purpleLight:"#f5f3ff",
};

// Status config — exact matches Courier.js model enums
const STATUS_MAP = {
  placed:          { label:"Placed",      bg:T.blueLight,    color:T.blue,    icon:Clock        },
  pickup_assigned: { label:"Rider Assigned", bg:T.yellowLight, color:T.yellow, icon:Bike         },
  picked_up:       { label:"Picked Up",   bg:T.purpleLight,  color:T.purple,  icon:Package      },
  in_transit:      { label:"In Transit",  bg:"#fdf4ff",      color:T.purple,  icon:Truck        },
  delivered:       { label:"Delivered",   bg:T.greenLight,   color:T.green,   icon:CheckCircle2 },
  cancelled:       { label:"Cancelled",   bg:T.redLight,     color:T.red,     icon:XCircle      },
};

// Status flow — next status mapping
const NEXT_STATUS = {
  placed:          "pickup_assigned",
  pickup_assigned: "picked_up",
  picked_up:       "in_transit",
  in_transit:      "delivered",
};

// Parcel types — exact matches Courier.js model enums
const PARCEL_MAP = {
  document: { label:"Document",  Icon:FileText, color:T.blue,    bg:T.blueLight    },
  small:    { label:"Small Box", Icon:Box,      color:T.green,   bg:T.greenLight   },
  medium:   { label:"Medium",    Icon:Gift,     color:T.purple,  bg:T.purpleLight  },
  heavy:    { label:"Heavy",     Icon:Weight,   color:T.red,     bg:T.redLight     },
};

// ── Assign Rider Modal ────────────────────────────
function AssignRiderModal({ order, riders, onClose, onAssign }) {
  const [riderId,  setRiderId]  = useState("");
  const [saving,   setSaving]   = useState(false);
  const online = riders.filter(r => r.isActive && r.isOnline);

  const handleAssign = async () => {
    if (!riderId) return alert("Rider select karo!");
    setSaving(true);
    // updateStatus with riderId → pickup_assigned
    const res = await api.courier.updateStatus(order._id, {
      status:  "pickup_assigned",
      riderId,
    });
    setSaving(false);
    if (res.success) onAssign(res.data);
    else alert(res.message || "Error!");
  };

  return (
    <div onClick={onClose} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center",padding:16 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:T.white,borderRadius:20,width:"100%",maxWidth:400,boxShadow:"0 24px 64px rgba(0,0,0,0.18)",overflow:"hidden" }}>
        <div style={{ padding:"16px 18px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between" }}>
          <div style={{ fontFamily:"'Fraunces',Georgia,serif",fontWeight:900,fontSize:"1rem",color:T.text }}>Assign Rider</div>
          <button onClick={onClose} style={{ background:T.bg,border:"none",borderRadius:"50%",width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer" }}>
            <X size={15} color={T.textSub}/>
          </button>
        </div>

        <div style={{ padding:18 }}>
          {/* Order mini summary */}
          <div style={{ background:T.bg,borderRadius:12,padding:"12px 14px",marginBottom:16 }}>
            <div style={{ fontSize:"0.65rem",fontWeight:700,color:T.textMuted,marginBottom:8 }}>ORDER {order.trackingId}</div>
            <div style={{ display:"flex",alignItems:"center",gap:7,marginBottom:5 }}>
              <div style={{ width:8,height:8,borderRadius:"50%",background:T.green,flexShrink:0 }}/>
              <div style={{ fontSize:"0.78rem",color:T.textSub }}><strong style={{ color:T.text }}>{order.pickupAddress?.name}</strong> · {order.pickupAddress?.address}</div>
            </div>
            <div style={{ display:"flex",alignItems:"center",gap:7 }}>
              <div style={{ width:8,height:8,borderRadius:"50%",background:T.red,flexShrink:0 }}/>
              <div style={{ fontSize:"0.78rem",color:T.textSub }}><strong style={{ color:T.text }}>{order.deliveryAddress?.name}</strong> · {order.deliveryAddress?.address}</div>
            </div>
          </div>

          <div style={{ fontSize:"0.7rem",fontWeight:700,color:T.textMuted,marginBottom:10 }}>
            ONLINE RIDERS ({online.length})
          </div>

          {online.length === 0 ? (
            <div style={{ textAlign:"center",padding:"20px 0",color:T.textMuted }}>
              <Bike size={36} color={T.border} style={{ marginBottom:8 }}/>
              <div style={{ fontSize:"0.82rem",fontWeight:600 }}>Koi rider online nahi hai</div>
            </div>
          ) : (
            <div style={{ display:"flex",flexDirection:"column",gap:8,marginBottom:16 }}>
              {online.map(r => (
                <div key={r._id} onClick={() => setRiderId(r._id)}
                  style={{ display:"flex",alignItems:"center",gap:10,padding:"11px 13px",borderRadius:12,border:`2px solid ${riderId===r._id?T.primary:T.border}`,background:riderId===r._id?T.primaryLight:T.white,cursor:"pointer",transition:"all .15s" }}>
                  <div style={{ width:38,height:38,borderRadius:"50%",background:`linear-gradient(135deg,${T.primaryDark},${T.primary})`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                    <span style={{ fontFamily:"'Fraunces',Georgia,serif",fontWeight:900,color:"#fff" }}>{r.name?.charAt(0)}</span>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700,fontSize:"0.85rem",color:T.text }}>{r.name}</div>
                    <div style={{ fontSize:"0.7rem",color:T.textSub }}>{r.area||"Akola"} · {r.vehicleType}</div>
                  </div>
                  <div style={{ display:"flex",alignItems:"center",gap:4 }}>
                    <div style={{ width:7,height:7,borderRadius:"50%",background:T.green }}/>
                    <span style={{ fontSize:"0.65rem",color:T.green,fontWeight:700 }}>Online</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <button onClick={handleAssign} disabled={saving || !riderId}
            style={{ width:"100%",background:riderId?`linear-gradient(135deg,${T.primaryDark},${T.primary})`:"#e2e8f0",border:"none",borderRadius:12,padding:"12px",color:riderId?"#fff":T.textMuted,fontWeight:800,fontSize:"0.88rem",cursor:riderId?"pointer":"not-allowed",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:6 }}>
            {saving ? <Loader2 size={15} className="spin"/> : <Bike size={15}/>}
            Assign Rider
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Order Detail Modal ────────────────────────────
function DetailModal({ order, riders, onClose, onUpdate }) {
  const [updating,    setUpdating]    = useState(false);
  const [showAssign,  setShowAssign]  = useState(false);
  const s      = STATUS_MAP[order.status] || STATUS_MAP.placed;
  const SIcon  = s.icon;
  const next   = NEXT_STATUS[order.status];
  const parcel = PARCEL_MAP[order.parcelType] || PARCEL_MAP.document;
  const PIcon  = parcel.Icon;

  const handleProgress = async () => {
    if (!next) return;
    setUpdating(true);
    const res = await api.courier.updateStatus(order._id, { status: next });
    setUpdating(false);
    if (res.success) onUpdate(res.data);
    else alert(res.message || "Error!");
  };

  const handleCancel = async () => {
    if (!window.confirm("Cancel karna hai?")) return;
    setUpdating(true);
    const res = await api.courier.updateStatus(order._id, { status:"cancelled" });
    setUpdating(false);
    if (res.success) onUpdate(res.data);
  };

  if (showAssign) return (
    <AssignRiderModal
      order={order} riders={riders}
      onClose={() => setShowAssign(false)}
      onAssign={(data) => { onUpdate(data); setShowAssign(false); }}
    />
  );

  return (
    <div onClick={onClose} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:400,display:"flex",alignItems:"center",justifyContent:"center",padding:16 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:T.white,borderRadius:20,width:"100%",maxWidth:460,maxHeight:"88vh",overflowY:"auto",boxShadow:"0 24px 64px rgba(0,0,0,0.18)" }}>

        {/* Header */}
        <div style={{ padding:"16px 18px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,background:T.white,zIndex:1 }}>
          <div>
            <div style={{ fontFamily:"'Fraunces',Georgia,serif",fontWeight:900,fontSize:"1rem",color:T.text }}>
              {order.trackingId}
            </div>
            <div style={{ fontSize:"0.68rem",color:T.textMuted,marginTop:1 }}>
              {new Date(order.createdAt).toLocaleString("en-IN")}
            </div>
          </div>
          <button onClick={onClose} style={{ background:T.bg,border:"none",borderRadius:"50%",width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer" }}>
            <X size={15} color={T.textSub}/>
          </button>
        </div>

        <div style={{ padding:18 }}>
          {/* Status + parcel type */}
          <div style={{ display:"flex",gap:8,marginBottom:16 }}>
            <span style={{ display:"inline-flex",alignItems:"center",gap:5,background:s.bg,color:s.color,fontWeight:700,fontSize:"0.78rem",padding:"5px 12px",borderRadius:100 }}>
              <SIcon size={12}/> {s.label}
            </span>
            <span style={{ display:"inline-flex",alignItems:"center",gap:5,background:parcel.bg,color:parcel.color,fontWeight:700,fontSize:"0.78rem",padding:"5px 12px",borderRadius:100 }}>
              <PIcon size={12}/> {parcel.label}
            </span>
            {order.isFragile && (
              <span style={{ display:"inline-flex",alignItems:"center",gap:4,background:T.redLight,color:T.red,fontWeight:700,fontSize:"0.72rem",padding:"5px 10px",borderRadius:100 }}>
                ⚠️ Fragile
              </span>
            )}
          </div>

          {/* Pickup → Drop visual */}
          <div style={{ background:T.bg,borderRadius:14,padding:"14px 16px",marginBottom:12 }}>
            <div style={{ display:"flex",gap:12 }}>
              <div style={{ display:"flex",flexDirection:"column",alignItems:"center",paddingTop:4 }}>
                <div style={{ width:11,height:11,borderRadius:"50%",background:T.green,boxShadow:`0 0 0 3px ${T.greenLight}` }}/>
                <div style={{ width:2,height:32,background:`linear-gradient(${T.green},${T.red})`,margin:"3px 0" }}/>
                <div style={{ width:11,height:11,borderRadius:"50%",background:T.red,boxShadow:`0 0 0 3px ${T.redLight}` }}/>
              </div>
              <div style={{ flex:1 }}>
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontSize:"0.6rem",fontWeight:700,color:T.textMuted,marginBottom:3 }}>PICKUP</div>
                  <div style={{ fontWeight:700,fontSize:"0.88rem",color:T.text }}>{order.pickupAddress?.name}</div>
                  <div style={{ fontSize:"0.75rem",color:T.textSub }}>{order.pickupAddress?.address}</div>
                  {order.pickupAddress?.landmark && <div style={{ fontSize:"0.7rem",color:T.textMuted }}>Near: {order.pickupAddress.landmark}</div>}
                  <a href={`tel:${order.pickupAddress?.phone}`} style={{ fontSize:"0.72rem",color:T.primary,fontWeight:600,display:"inline-flex",alignItems:"center",gap:4,marginTop:3,textDecoration:"none" }}>
                    <Phone size={10}/> {order.pickupAddress?.phone}
                  </a>
                </div>
                <div>
                  <div style={{ fontSize:"0.6rem",fontWeight:700,color:T.textMuted,marginBottom:3 }}>DROP</div>
                  <div style={{ fontWeight:700,fontSize:"0.88rem",color:T.text }}>{order.deliveryAddress?.name}</div>
                  <div style={{ fontSize:"0.75rem",color:T.textSub }}>{order.deliveryAddress?.address}</div>
                  {order.deliveryAddress?.landmark && <div style={{ fontSize:"0.7rem",color:T.textMuted }}>Near: {order.deliveryAddress.landmark}</div>}
                  <a href={`tel:${order.deliveryAddress?.phone}`} style={{ fontSize:"0.72rem",color:T.primary,fontWeight:600,display:"inline-flex",alignItems:"center",gap:4,marginTop:3,textDecoration:"none" }}>
                    <Phone size={10}/> {order.deliveryAddress?.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Parcel + Payment info */}
          <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:12 }}>
            {[
              { l:"Amount",  v:`Rs.${order.price}` },
              { l:"Payment", v:(order.paymentMode||"COD").toUpperCase() },
              { l:"Paid",    v:order.isPaid?"Yes":"No", c:order.isPaid?T.green:T.red },
            ].map((d,i) => (
              <div key={i} style={{ background:T.bg,borderRadius:10,padding:"10px",textAlign:"center" }}>
                <div style={{ fontWeight:800,fontSize:"0.9rem",color:d.c||T.text }}>{d.v}</div>
                <div style={{ fontSize:"0.62rem",color:T.textMuted,marginTop:2 }}>{d.l}</div>
              </div>
            ))}
          </div>

          {/* Description */}
          {order.description && (
            <div style={{ background:T.bg,borderRadius:10,padding:"10px 12px",marginBottom:12,fontSize:"0.78rem",color:T.textSub }}>
              <span style={{ fontWeight:700,color:T.text }}>Note: </span>{order.description}
            </div>
          )}

          {/* Assigned Rider */}
          {order.rider?.name && (
            <div style={{ background:T.blueLight,borderRadius:12,padding:"12px 14px",marginBottom:14,display:"flex",alignItems:"center",gap:10 }}>
              <div style={{ width:38,height:38,borderRadius:10,background:T.blue,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                <Bike size={18} color="#fff"/>
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:"0.6rem",color:T.blue,fontWeight:700,marginBottom:2 }}>ASSIGNED RIDER</div>
                <div style={{ fontWeight:700,fontSize:"0.88rem",color:T.text }}>{order.rider.name}</div>
                <a href={`tel:${order.rider.phone}`} style={{ fontSize:"0.72rem",color:T.blue,fontWeight:600,display:"inline-flex",alignItems:"center",gap:4,textDecoration:"none" }}>
                  <Phone size={10}/> {order.rider.phone}
                </a>
              </div>
            </div>
          )}

          {/* Customer info */}
          {order.user && (
            <div style={{ background:T.bg,borderRadius:10,padding:"10px 12px",marginBottom:14,display:"flex",alignItems:"center",gap:10 }}>
              <User size={16} color={T.textMuted}/>
              <div style={{ fontSize:"0.78rem",color:T.textSub }}>
                <strong style={{ color:T.text }}>{order.user.name||"Customer"}</strong> · {order.user.phone||"—"}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
            {/* Assign rider */}
            {order.status === "placed" && (
              <button onClick={() => setShowAssign(true)}
                style={{ background:`linear-gradient(135deg,${T.primaryDark},${T.primary})`,border:"none",borderRadius:12,padding:"12px",color:"#fff",fontWeight:800,fontSize:"0.88rem",cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:6 }}>
                <Bike size={16}/> Assign Rider
              </button>
            )}

            {/* Progress button */}
            {next && order.status !== "placed" && (
              <button onClick={handleProgress} disabled={updating}
                style={{ background:`linear-gradient(135deg,${T.primaryDark},${T.primary})`,border:"none",borderRadius:12,padding:"12px",color:"#fff",fontWeight:800,fontSize:"0.88rem",cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:6 }}>
                {updating ? <Loader2 size={15} className="spin"/> : <Check size={15}/>}
                Mark as {STATUS_MAP[next]?.label}
              </button>
            )}

            {/* Cancel */}
            {["placed","pickup_assigned"].includes(order.status) && (
              <button onClick={handleCancel} disabled={updating}
                style={{ background:T.redLight,border:`1px solid #fecaca`,borderRadius:12,padding:"11px",color:T.red,fontWeight:700,fontSize:"0.82rem",cursor:"pointer",fontFamily:"inherit" }}>
                Cancel Order
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Courier Admin Page ───────────────────────
export default function Courier() {
  const [orders,  setOrders]  = useState([]);
  const [riders,  setRiders]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState("all");
  const [search,  setSearch]  = useState("");
  const [detail,  setDetail]  = useState(null);
  const [toast,   setToast]   = useState("");
  const [page,    setPage]    = useState(1);
  const [total,   setTotal]   = useState(0);

  // Stats
  const [stats, setStats] = useState({ placed:0, pickup_assigned:0, picked_up:0, in_transit:0, delivered:0, cancelled:0 });

  const showToast = m => { setToast(m); setTimeout(() => setToast(""), 2500); };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page, limit:20,
        ...(filter !== "all" ? { status:filter } : {}),
      });
      const [cRes, rRes] = await Promise.all([
        api.courier.getAll(params.toString()),
        api.riders.getAll(),
      ]);

      if (cRes.success) {
        setOrders(cRes.couriers || []);
        setTotal(cRes.total || 0);
        // Build stats from orders
        const all = cRes.couriers || [];
        const s = {};
        Object.keys(STATUS_MAP).forEach(k => { s[k] = all.filter(o => o.status===k).length; });
        setStats(s);
      }
      if (rRes.success) setRiders(rRes.data || []);
    } catch { showToast("Backend connect nahi hua"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter, page]);

  const handleUpdate = (updated) => {
    setOrders(o => o.map(x => x._id===updated._id ? updated : x));
    if (detail?._id === updated._id) setDetail(updated);
    showToast("Updated!");
  };

  const filtered = orders.filter(o =>
    !search ||
    o.trackingId?.toLowerCase().includes(search.toLowerCase()) ||
    o.pickupAddress?.name?.toLowerCase().includes(search.toLowerCase()) ||
    o.pickupAddress?.phone?.includes(search)
  );

  return (
    <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <style>{`
        @keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
        .spin{animation:spin 1s linear infinite}
        .ocard{transition:box-shadow .2s,transform .15s}
        .ocard:hover{box-shadow:0 6px 24px rgba(0,0,0,0.09)!important;transform:translateY(-1px)}
      `}</style>

      {toast && (
        <div style={{ position:"fixed",top:80,left:"50%",transform:"translateX(-50%)",background:T.text,color:"#fff",padding:"10px 22px",borderRadius:100,fontSize:"0.82rem",fontWeight:600,zIndex:600,display:"flex",alignItems:"center",gap:7,boxShadow:"0 8px 24px rgba(0,0,0,0.15)" }}>
          <Check size={14} color={T.green}/> {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20,flexWrap:"wrap",gap:12 }}>
        <div>
          <div style={{ fontFamily:"'Fraunces',Georgia,serif",fontWeight:900,fontSize:"1.2rem",color:T.text }}>Courier Orders</div>
          <div style={{ fontSize:"0.72rem",color:T.textMuted,marginTop:2 }}>
            {total} total · Live from MongoDB
          </div>
        </div>
        <button onClick={load}
          style={{ background:T.white,border:`1px solid ${T.border}`,borderRadius:10,padding:"8px 16px",cursor:"pointer",display:"flex",alignItems:"center",gap:6,fontSize:"0.78rem",fontWeight:600,color:T.textSub,fontFamily:"inherit" }}>
          <RefreshCw size={14} className={loading?"spin":""}/> Refresh
        </button>
      </div>

      {/* Stats row */}
      <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(110px,1fr))",gap:10,marginBottom:20 }}>
        {[
          { key:"placed",          label:"Placed",      color:T.blue,   bg:T.blueLight    },
          { key:"pickup_assigned", label:"Assigned",    color:T.yellow, bg:T.yellowLight  },
          { key:"picked_up",       label:"Picked Up",   color:T.purple, bg:T.purpleLight  },
          { key:"in_transit",      label:"In Transit",  color:T.primary,bg:T.primaryLight },
          { key:"delivered",       label:"Delivered",   color:T.green,  bg:T.greenLight   },
          { key:"cancelled",       label:"Cancelled",   color:T.red,    bg:T.redLight     },
        ].map((x) => (
          <div key={x.key} onClick={() => { setFilter(x.key); setPage(1); }}
            style={{ background:filter===x.key?x.bg:T.white, borderRadius:12, padding:"12px 10px", border:`1.5px solid ${filter===x.key?x.color:T.border}`, cursor:"pointer", transition:"all .2s", textAlign:"center" }}>
            <div style={{ fontWeight:900,fontSize:"1.4rem",color:x.color,fontFamily:"'Fraunces',Georgia,serif" }}>
              {loading ? "—" : stats[x.key]||0}
            </div>
            <div style={{ fontSize:"0.65rem",color:T.textSub,fontWeight:600,marginTop:2 }}>{x.label}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs + Search */}
      <div style={{ display:"flex",gap:8,marginBottom:14,flexWrap:"wrap",alignItems:"center" }}>
        <button onClick={() => { setFilter("all"); setPage(1); }}
          style={{ background:filter==="all"?T.primary:T.white,color:filter==="all"?"#fff":T.textSub,border:`1.5px solid ${filter==="all"?T.primary:T.border}`,borderRadius:100,padding:"6px 16px",fontWeight:700,fontSize:"0.78rem",cursor:"pointer",fontFamily:"inherit",transition:"all .2s" }}>
          All ({total})
        </button>

        {/* Search */}
        <div style={{ display:"flex",alignItems:"center",gap:8,background:T.white,border:`1.5px solid ${T.border}`,borderRadius:10,padding:"7px 12px",flex:1,maxWidth:280 }}>
          <Search size={14} color={T.textMuted}/>
          <input placeholder="Tracking ID ya naam search..." value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ border:"none",background:"transparent",color:T.text,fontSize:"0.82rem",outline:"none",fontFamily:"inherit",width:"100%" }}/>
          {search && <button onClick={() => setSearch("")} style={{ background:"none",border:"none",cursor:"pointer",display:"flex" }}><X size={13} color={T.textMuted}/></button>}
        </div>
      </div>

      {/* Orders Grid */}
      {loading ? (
        <div style={{ textAlign:"center",padding:60,color:T.textMuted }}>
          <Loader2 size={32} color={T.primary} className="spin" style={{ marginBottom:10 }}/>
          <div style={{ fontSize:"0.85rem",fontWeight:600 }}>Loading orders...</div>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:"center",padding:60 }}>
          <Package size={48} color={T.border} strokeWidth={1} style={{ marginBottom:12 }}/>
          <div style={{ fontWeight:700,color:T.textSub,fontSize:"0.95rem" }}>Koi orders nahi</div>
          <div style={{ fontSize:"0.78rem",color:T.textMuted,marginTop:4 }}>
            {filter !== "all" ? `"${STATUS_MAP[filter]?.label}" status mein koi order nahi` : "Abhi tak koi courier booking nahi"}
          </div>
        </div>
      ) : (
        <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:14 }}>
          {filtered.map((o, i) => {
            const s      = STATUS_MAP[o.status] || STATUS_MAP.placed;
            const parcel = PARCEL_MAP[o.parcelType] || PARCEL_MAP.document;
            const SIcon  = s.icon;
            const PIcon  = parcel.Icon;

            return (
              <div key={o._id} className="ocard"
                style={{ background:T.white,borderRadius:16,border:`1.5px solid ${o.status==="placed"?T.primaryMid:T.border}`,padding:"16px",boxShadow:"0 2px 8px rgba(0,0,0,0.04)",animation:`fadeUp .3s ${i*.04}s ease both`,cursor:"pointer" }}
                onClick={() => setDetail(o)}>

                {/* Top row */}
                <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12 }}>
                  <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                    <div style={{ width:38,height:38,borderRadius:11,background:parcel.bg,display:"flex",alignItems:"center",justifyContent:"center" }}>
                      <PIcon size={18} color={parcel.color} strokeWidth={1.8}/>
                    </div>
                    <div>
                      <div style={{ fontWeight:800,fontSize:"0.85rem",color:T.text }}>{o.trackingId}</div>
                      <div style={{ fontSize:"0.65rem",color:T.textMuted }}>{new Date(o.createdAt).toLocaleDateString("en-IN",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</div>
                    </div>
                  </div>
                  <span style={{ display:"inline-flex",alignItems:"center",gap:4,background:s.bg,color:s.color,fontSize:"0.65rem",fontWeight:700,padding:"3px 9px",borderRadius:100 }}>
                    <SIcon size={10}/> {s.label}
                  </span>
                </div>

                {/* Pickup → Drop */}
                <div style={{ marginBottom:12 }}>
                  <div style={{ display:"flex",alignItems:"flex-start",gap:8,marginBottom:7 }}>
                    <div style={{ width:8,height:8,borderRadius:"50%",background:T.green,flexShrink:0,marginTop:4 }}/>
                    <div style={{ fontSize:"0.78rem",lineHeight:1.4 }}>
                      <strong style={{ color:T.text }}>{o.pickupAddress?.name}</strong>
                      <span style={{ color:T.textSub }}> · {o.pickupAddress?.address}</span>
                    </div>
                  </div>
                  <div style={{ display:"flex",alignItems:"flex-start",gap:8 }}>
                    <div style={{ width:8,height:8,borderRadius:"50%",background:T.red,flexShrink:0,marginTop:4 }}/>
                    <div style={{ fontSize:"0.78rem",lineHeight:1.4 }}>
                      <strong style={{ color:T.text }}>{o.deliveryAddress?.name}</strong>
                      <span style={{ color:T.textSub }}> · {o.deliveryAddress?.address}</span>
                    </div>
                  </div>
                </div>

                {/* Info chips */}
                <div style={{ display:"flex",gap:7,marginBottom:12,flexWrap:"wrap" }}>
                  <span style={{ background:T.bg,borderRadius:7,padding:"4px 9px",fontSize:"0.68rem",fontWeight:700,color:T.text }}>
                    Rs.{o.price}
                  </span>
                  <span style={{ background:T.bg,borderRadius:7,padding:"4px 9px",fontSize:"0.68rem",fontWeight:600,color:T.textSub,textTransform:"uppercase" }}>
                    {o.paymentMode||"COD"}
                  </span>
                  {o.isFragile && (
                    <span style={{ background:T.redLight,borderRadius:7,padding:"4px 9px",fontSize:"0.68rem",fontWeight:700,color:T.red }}>
                      ⚠️ Fragile
                    </span>
                  )}
                </div>

                {/* Rider or Assign button */}
                {o.rider?.name ? (
                  <div style={{ display:"flex",alignItems:"center",gap:7,background:T.blueLight,borderRadius:9,padding:"7px 10px" }}>
                    <Bike size={13} color={T.blue}/>
                    <span style={{ fontSize:"0.75rem",fontWeight:700,color:T.blue }}>{o.rider.name}</span>
                  </div>
                ) : o.status === "placed" ? (
                  <button onClick={e => { e.stopPropagation(); setDetail(o); }}
                    style={{ width:"100%",background:T.primary,border:"none",borderRadius:9,padding:"8px",fontWeight:700,fontSize:"0.78rem",cursor:"pointer",color:"#fff",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:5 }}>
                    <Bike size={13}/> Assign Rider
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {total > 20 && (
        <div style={{ display:"flex",justifyContent:"center",gap:8,marginTop:18 }}>
          <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}
            style={{ background:T.white,border:`1px solid ${T.border}`,borderRadius:8,padding:"7px 16px",cursor:page===1?"not-allowed":"pointer",fontFamily:"inherit",fontSize:"0.78rem",color:T.textSub,opacity:page===1?0.5:1 }}>
            ← Prev
          </button>
          <span style={{ padding:"7px 14px",fontSize:"0.78rem",color:T.textSub,fontWeight:600 }}>
            {page} / {Math.ceil(total/20)}
          </span>
          <button onClick={() => setPage(p => p+1)} disabled={page>=Math.ceil(total/20)}
            style={{ background:T.white,border:`1px solid ${T.border}`,borderRadius:8,padding:"7px 16px",cursor:"pointer",fontFamily:"inherit",fontSize:"0.78rem",color:T.textSub }}>
            Next →
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {detail && (
        <DetailModal
          order={detail}
          riders={riders}
          onClose={() => setDetail(null)}
          onUpdate={handleUpdate}
        />
      )}
    </div>
  );
}